import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  PlayIcon,
  UserGroupIcon,
  ChartBarIcon,
  CpuChipIcon,
  IdentificationIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, statusBadge, feedStatusColor } from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'
import GamingBackgroundJobsPanel from '../monitor-safe/components/GamingBackgroundJobsPanel'

function FeedCard({ feed }) {
  const icons = {
    egm: ServerStackIcon,
    patron: UserGroupIcon,
    visitor: IdentificationIcon,
    sessions: ChartBarIcon,
    meter: CpuChipIcon,
    'slot-analysis': ChartBarIcon,
  }
  const FeedIcon = icons[feed.id] || UserGroupIcon

  if (feed.coming_soon) {
    return (
      <div className={`rounded-lg border p-5 ${feedStatusColor('coming_soon')}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">{feed.label}</h3>
            <p className="text-sm text-gray-500 mt-1">{feed.description}</p>
          </div>
          {statusBadge('coming_soon')}
        </div>
        <p className="text-xs text-gray-400 mt-4">Not available for this tenant yet.</p>
      </div>
    )
  }

  const href = feed.id === 'patron'
    ? '/monitor-safe/daily-data/dimpatron'
    : `/monitor-safe/daily-data/${feed.id}`

  return (
    <div className={`rounded-lg border p-5 ${feedStatusColor(feed.status)}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-white/80 p-2 border border-gray-200">
            <FeedIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{feed.label}</h3>
            <p className="text-sm text-gray-600 mt-0.5">{feed.description}</p>
          </div>
        </div>
        {statusBadge(feed.status)}
      </div>

      <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Trading day</dt>
          <dd className="font-medium text-gray-900">{formatTradingDay(feed.trading_day_today)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Last run</dt>
          <dd className="font-medium text-gray-900">{formatDateTime(feed.last_run_at)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Template / source</dt>
          <dd className="font-medium text-gray-900 truncate" title={feed.template_name}>
            {feed.id === 'visitor' && feed.visitor_source === 'api'
              ? 'RXConnect API'
              : (feed.template_name || 'Default')}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Schedule</dt>
          <dd className="font-medium text-gray-900 font-mono text-xs">
            {feed.scheduler_enabled ? (feed.schedule || '—') : 'Manual only'}
          </dd>
        </div>
      </dl>

      {feed.last_success && (
        <p className="text-xs text-gray-600 mt-3">
          Last success: {feed.last_success.imported} imported
          {feed.last_success.updated != null && feed.id === 'egm' && `, ${feed.last_success.updated} denoms synced`}
          {feed.last_success.updated != null && feed.id !== 'egm' && `, ${feed.last_success.updated} updated`}
          {feed.last_success.skipped != null && `, ${feed.last_success.skipped} skipped`}
          {feed.last_success.errors > 0 && `, ${feed.last_success.errors} errors`}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={href}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
        >
          Open
          <ArrowRightIcon className="w-4 h-4 ml-1" />
        </Link>
        {feed.id === 'meter' && (
          <Link
            to="/monitor-safe/daily-data/turnover"
            className="inline-flex items-center px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm font-medium hover:bg-indigo-100"
          >
            Daily turnover
            <ArrowRightIcon className="w-4 h-4 ml-1" />
          </Link>
        )}
      </div>
    </div>
  )
}

function PipelineStatusCard({ pipeline, tradingDay }) {
  if (!pipeline) return null

  const state = pipeline.pipeline_state || 'waiting_ingest'
  const border =
    state === 'rules_ready' ? 'border-green-200 bg-green-50'
      : state === 'running' ? 'border-blue-200 bg-blue-50'
        : (state.includes('failed') || state === 'failed') ? 'border-red-200 bg-red-50'
          : 'border-amber-200 bg-amber-50'

  return (
    <div className={`rounded-lg border p-5 ${border}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Transaction monitoring pipeline</h2>
          <p className="text-sm text-gray-600 mt-1">
            Ingest → enrich → allocate → rules-ready for trading day {formatTradingDay(tradingDay || pipeline.trading_day)}.
          </p>
        </div>
        {statusBadge(state)}
      </div>
      <dl className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 text-xs uppercase">Meter ingest</dt>
          <dd className="font-medium">{pipeline.ingest_meter_ok ? 'OK' : 'Pending'}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Sessions ingest</dt>
          <dd className="font-medium">{pipeline.ingest_sessions_ok ? 'OK' : 'Pending'}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Enriched rows</dt>
          <dd className="font-medium">{pipeline.enriched_count ?? 0}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Attribution rows</dt>
          <dd className="font-medium">{pipeline.attribution_count ?? 0}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Auto pipeline</dt>
          <dd className="font-medium">{pipeline.auto_pipeline_enabled ? 'On' : 'Off'}</dd>
        </div>
      </dl>
      {pipeline.last_error && (
        <p className="text-sm text-red-700 mt-3">{pipeline.last_error}</p>
      )}
      {!pipeline.rules_ready && state !== 'running' && (
        <p className="text-xs text-gray-600 mt-3">
          Run <strong>Ingest this day</strong> below (includes combine/rollup), or use Gaming Data → Process &amp; Combine.
        </p>
      )}
    </div>
  )
}

const FEED_RESULT_ORDER = [
  ['egm', 'EGM'],
  ['patron', 'DimPatron'],
  ['visitor', 'Visitors'],
  ['sessions', 'Sessions'],
  ['meter', 'Meter'],
  ['slot_analysis', 'Slot analysis'],
  ['pipeline', 'Combine / rollup'],
]

const TERMINAL_JOB_STATUS = new Set(['success', 'partial', 'failed', 'skipped'])

function progressLabel(state) {
  if (state === 'running') return 'running…'
  if (state === 'done') return 'done'
  if (state === 'failed') return 'failed'
  if (state === 'skipped') return 'skipped'
  return 'pending'
}

function isFeedLevelSkip(row) {
  return row?.skipped === true && typeof row?.reason === 'string'
}

function feedResultDetail(key, row) {
  if (!row) return null
  if (isFeedLevelSkip(row)) return `skipped (${row.reason})`
  if (row.error) return `failed: ${row.error}`
  if (key === 'pipeline') {
    if (row.ok === false) return `failed: ${row.error || 'pipeline error'}`
    if (isFeedLevelSkip(row)) return `skipped (${row.reason})`
    return 'ok'
  }
  if (row.imported != null) {
    let detail = `${row.imported} imported`
    if (row.updated != null) detail += `, ${row.updated} updated`
    if (Number(row.skipped) > 0) detail += `, ${row.skipped} rows skipped`
    if (row.errors) detail += `, ${row.errors} errors`
    return detail
  }
  return 'done'
}

function IngestDayProgress({ job }) {
  if (!job) return null
  const stage = job.stage || job.status
  const stageLine =
    stage === 'egm' ? 'Loading EGM registry…'
      : stage === 'feeds_parallel' ? 'Loading DimPatron, visitors, sessions, meter, and slot in parallel…'
        : stage === 'pipeline' ? 'Running combine / allocate / rollup…'
          : stage === 'starting' || stage === 'queued' ? 'Starting…'
            : null

  return (
    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
        <ArrowPathIcon className="w-4 h-4 animate-spin shrink-0" />
        Ingesting {formatTradingDay(job.trading_day)}
        {stageLine && <span className="font-normal text-blue-800">— {stageLine}</span>}
      </div>
      <ul className="mt-3 space-y-1 text-sm">
        {FEED_RESULT_ORDER.map(([key, label]) => {
          const state = key === 'pipeline'
            ? (stage === 'pipeline' ? (job.progress?.pipeline || 'running') : job.progress?.pipeline)
            : job.progress?.[key]
          if (!state) return null
          const tone = state === 'failed'
            ? 'text-red-700'
            : state === 'done'
              ? 'text-green-800'
              : state === 'skipped'
                ? 'text-amber-800'
                : 'text-blue-800'
          return (
            <li key={key} className="flex gap-2">
              <span className="w-32 shrink-0 font-medium text-gray-800">{label}</span>
              <span className={tone}>{progressLabel(state)}</span>
            </li>
          )
        })}
      </ul>
      <p className="mt-2 text-xs text-blue-800">
        Running in the background — you can leave this page open or refresh later; progress is polled automatically.
      </p>
    </div>
  )
}

function IngestDayResult({ result }) {
  if (!result) return null
  const errors = Array.isArray(result.errors) ? result.errors : []
  const status = result.overall_status || (errors.length ? 'partial' : 'success')
  const border =
    status === 'success' ? 'border-green-200 bg-green-50'
      : status === 'skipped' ? 'border-amber-200 bg-amber-50'
        : 'border-amber-200 bg-amber-50'

  return (
    <div className={`rounded-lg border p-4 ${border}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Ingest result — {formatTradingDay(result.trading_day)}
        </h3>
        <span className="text-xs text-gray-600">
          {result.duration_ms != null ? `${Math.round(result.duration_ms / 1000)}s` : ''}
          {status === 'partial' ? ' · partial' : status === 'skipped' ? ' · skipped' : ' · ok'}
        </span>
      </div>
      <ul className="mt-3 space-y-1.5 text-sm">
        {FEED_RESULT_ORDER.map(([key, label]) => {
          const row = result[key]
          if (!row) return null
          const detail = feedResultDetail(key, row)
          return (
            <li key={key} className="flex gap-2">
              <span className="w-32 shrink-0 font-medium text-gray-800">{label}</span>
              <span className={row.error || row.ok === false ? 'text-red-700' : 'text-gray-700'}>{detail}</span>
            </li>
          )
        })}
      </ul>
      {errors.length > 0 && (
        <p className="mt-2 text-xs text-amber-900">
          {errors.length} step(s) reported errors — open individual feeds for detail.
        </p>
      )}
    </div>
  )
}

function defaultIngestDay(overviewDay) {
  if (overviewDay && /^\d{4}-\d{2}-\d{2}$/.test(String(overviewDay))) {
    // Prefer previous calendar day relative to "trading day today" when offset is yesterday-style.
    const [y, m, d] = String(overviewDay).split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
    dt.setUTCDate(dt.getUTCDate() - 1)
    return dt.toISOString().slice(0, 10)
  }
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

export default function DailyData() {
  const { can } = useAuth()
  const canManageSettings = can('admin/system', 'view')
  const canIngest = can('monitor-safe/gaming-data', 'view')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [ingestDay, setIngestDay] = useState('')
  const [ingesting, setIngesting] = useState(false)
  const [ingestJob, setIngestJob] = useState(null)
  const [ingestError, setIngestError] = useState(null)
  const [ingestResult, setIngestResult] = useState(null)
  const pollRef = useRef(null)
  const [runPipeline, setRunPipeline] = useState(true)
  const [includeSlot, setIncludeSlot] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/daily-data/overview'))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load Daily Data')
      setData(json.data)
      setIngestDay((prev) => prev || json.data?.trading_day_yesterday || defaultIngestDay(json.data?.trading_day_today))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const pollIngestJob = useCallback((jobId) => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(getLocalApiUrl(`/api/daily-data/ingest-day/${jobId}`))
        const json = await res.json()
        if (!json.success || !json.data?.job) return
        const job = json.data.job
        setIngestJob(job)
        if (TERMINAL_JOB_STATUS.has(job.status)) {
          stopPolling()
          setIngesting(false)
          if (job.result) setIngestResult(job.result)
          if (job.error && !job.result) setIngestError(job.error)
          await load()
        }
      } catch {
        // keep polling through transient errors
      }
    }, 2500)
  }, [load, stopPolling])

  useEffect(() => {
    if (!canIngest) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/daily-data/ingest-day/active'))
        const json = await res.json()
        if (cancelled || !json.success || !json.data?.job) return
        const job = json.data.job
        if (TERMINAL_JOB_STATUS.has(job.status)) return
        setIngestJob(job)
        setIngesting(true)
        if (job.trading_day) setIngestDay(job.trading_day)
        pollIngestJob(job.job_id)
      } catch {
        // ignore
      }
    })()
    return () => {
      cancelled = true
      stopPolling()
    }
  }, [canIngest, pollIngestJob, stopPolling])

  const schedulerOn = useMemo(
    () => (data?.feeds || []).some((f) => f.scheduler_enabled),
    [data]
  )

  const runIngestDay = async () => {
    if (!ingestDay) {
      setIngestError('Choose a trading day')
      return
    }
    const label = formatTradingDay(ingestDay)
    if (!window.confirm(
      `Ingest all feeds for trading day ${label}?\n\n` +
      `EGM first, then DimPatron + visitors + sessions + meter` +
      (includeSlot ? ' + slot analysis' : '') +
      ' in parallel (all batch files for the day)' +
      (runPipeline ? ', then combine/rollup' : '') +
      '.\n\nRuns in the background — progress updates on this page.'
    )) return

    setIngesting(true)
    setIngestError(null)
    setIngestResult(null)
    setIngestJob(null)
    stopPolling()
    try {
      const res = await authFetch(getLocalApiUrl('/api/daily-data/ingest-day'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trading_day: ingestDay,
          include_slot_analysis: includeSlot,
          run_pipeline: runPipeline,
        }),
      })
      const json = await res.json()
      if (res.status === 409 && json.data?.job_id) {
        setIngestJob(json.data)
        pollIngestJob(json.data.job_id)
        return
      }
      if (!json.success) throw new Error(json.error || 'Ingest failed')
      if (res.status !== 202) throw new Error('Unexpected ingest response')
      setIngestJob(json.data)
      pollIngestJob(json.data.job_id)
    } catch (e) {
      setIngestError(e.message || 'Ingest failed')
      setIngesting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DailyDataNav />
      <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daily Data</h1>
            <p className="text-gray-600 mt-1">
              Vendor extracts by trading day — DimPatron, gaming sessions, and meter readings.
            </p>
            {data?.trading_day_today && (
              <p className="text-sm text-gray-500 mt-2 inline-flex items-center gap-1">
                <CalendarDaysIcon className="w-4 h-4" />
                Today&apos;s trading day: <span className="font-medium">{formatTradingDay(data.trading_day_today)}</span>
                <span className="text-gray-400">({data.timezone})</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canManageSettings && (
              <Link
                to="/monitor-safe/daily-data/settings"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-1" />
                Settings
              </Link>
            )}
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
      )}

      {canIngest && (
        <div className="bg-white shadow rounded-lg border border-gray-200 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Ingest this day</h2>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                One-click catch-up when scheduled auto-ingest is off (or for a re-run).
                EGM loads first; DimPatron and gaming feeds run in parallel, then optional combine/rollup.
                Runs in the background with live progress.
              </p>
              {!schedulerOn && (
                <p className="mt-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  Scheduler appears off — this button is the intended way to load a trading day.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ingest-trading-day">
                  Trading day
                </label>
                <input
                  id="ingest-trading-day"
                  type="date"
                  value={ingestDay}
                  onChange={(e) => setIngestDay(e.target.value)}
                  disabled={ingesting}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={runIngestDay}
                disabled={ingesting || !ingestDay}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
              >
                {ingesting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />
                    Ingesting…
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4 mr-1.5" />
                    Ingest this day
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-700">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSlot}
                onChange={(e) => setIncludeSlot(e.target.checked)}
                disabled={ingesting}
                className="rounded border-gray-300"
              />
              Include slot analysis
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={runPipeline}
                onChange={(e) => setRunPipeline(e.target.checked)}
                disabled={ingesting}
                className="rounded border-gray-300"
              />
              Run combine / allocate / rollup after ingest
            </label>
          </div>
          {ingesting && ingestJob && <IngestDayProgress job={ingestJob} />}
          {ingestError && (
            <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {ingestError}
            </div>
          )}
          {ingestResult && (
            <div className="mt-3">
              <IngestDayResult result={ingestResult} />
            </div>
          )}
        </div>
      )}

      {data?.pipeline && (
        <PipelineStatusCard pipeline={data.pipeline} tradingDay={data.trading_day_today} />
      )}

      <GamingBackgroundJobsPanel compact onChanged={load} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(data?.feeds || []).map(feed => (
          <FeedCard key={feed.id} feed={{ ...feed, trading_day_today: data?.trading_day_today }} />
        ))}
      </div>

      {!data?.config && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Import configuration is not provisioned for this tenant. Run migration 010 and configure
          {' '}
          <Link to="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</Link>.
        </div>
      )}
    </div>
  )
}
